/* One product image at a time, using the theme's Swiper and PhotoSwipe. */
(() => {
  'use strict';
  if (window.MTProductGallery) return;
  const instances = new WeakMap();
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init(root) {
    if (instances.has(root)) return;
    const main = root.querySelector('.mt-pdp-gallery__main');
    const slides = Array.from(root.querySelectorAll('[data-gallery-slide]'));
    const thumbs = Array.from(root.querySelectorAll('[data-gallery-thumb]'));
    const previous = root.querySelector('[data-gallery-prev]');
    const next = root.querySelector('[data-gallery-next]');
    const counter = root.querySelector('[data-gallery-counter]');
    const rail = root.querySelector('.mt-pdp-gallery__thumbs');
    const railPrevious = root.querySelector('[data-gallery-thumbs-prev]');
    const railNext = root.querySelector('[data-gallery-thumbs-next]');
    let active = Math.max(0, slides.findIndex(slide => slide.dataset.mediaId === root.dataset.selectedMedia));
    let swiper;
    let lightbox;

    function pauseMedia(except) {
      slides.forEach((slide, index) => {
        if (index === except) return;
        slide.querySelectorAll('video').forEach(video => video.pause());
        const player = slide.querySelector('[data-gallery-player]');
        if (player && player.querySelector('iframe')) {
          player.replaceChildren();
          player.hidden = true;
          slide.querySelector('[data-gallery-play]').hidden = false;
        }
        slide.querySelectorAll('model-viewer').forEach(model => model.pause?.());
      });
    }

    function prepareImage(index, isActive = false) {
      const image = slides[index]?.querySelector('.mt-pdp-gallery__image');
      if (!image) return;
      image.fetchPriority = isActive ? 'high' : 'low';
      image.loading = 'eager';
      image.decoding = 'async';
      if (image.dataset.gallerySrcset) image.srcset = image.dataset.gallerySrcset;
      if (image.dataset.gallerySrc) image.src = image.dataset.gallerySrc;
      delete image.dataset.gallerySrcset;
      delete image.dataset.gallerySrc;
    }

    function prepareNeighbours() {
      prepareImage(active, true);
      prepareImage(active + 1);
      prepareImage(active - 1);
    }

    function paintRail() {
      if (!rail) return;
      if (railPrevious) railPrevious.disabled = rail.scrollLeft <= 1;
      if (railNext) railNext.disabled = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 1;
    }

    function paint(scrollThumb) {
      prepareNeighbours();
      thumbs.forEach((thumb, index) => {
        thumb.classList.toggle('is-active', index === active);
        if (index === active) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      slides.forEach((slide, index) => {
        slide.setAttribute('aria-hidden', String(index !== active));
        slide.inert = index !== active;
      });
      if (counter) counter.textContent = `${active + 1} / ${slides.length}`;
      if (previous) previous.disabled = active === 0;
      if (next) next.disabled = active === slides.length - 1;
      pauseMedia(active);
      if (scrollThumb && rail && thumbs[active]) {
        const thumb = thumbs[active];
        const left = thumb.getBoundingClientRect().left - rail.getBoundingClientRect().left + rail.scrollLeft;
        if (left < rail.scrollLeft || left + thumb.offsetWidth > rail.scrollLeft + rail.clientWidth) {
          rail.scrollTo({ left:Math.max(0, left - (rail.clientWidth - thumb.offsetWidth) / 2), behavior:reducedMotion() ? 'auto' : 'smooth' });
        }
      }
      paintRail();
    }

    function focusActiveMedia() {
      const slide = slides[active];
      const player = slide.querySelector('[data-gallery-player]');
      const control = player && !player.hidden ? player.querySelector('video, iframe, model-viewer') : slide.querySelector('[data-gallery-zoom], [data-gallery-play]');
      control?.focus({ preventScroll:true });
    }

    function select(index, focusOrigin = false) {
      active = Math.max(0, Math.min(index, slides.length - 1));
      prepareNeighbours();
      if (swiper) swiper.slideTo(active, reducedMotion() ? 0 : 300);
      else {
        main.querySelector('.swiper-wrapper').style.transform = `translateX(-${active * 100}%)`;
        paint(true);
      }
      if (focusOrigin === true) thumbs[active]?.focus({ preventScroll:true });
      else if (focusOrigin === 'media') focusActiveMedia();
    }

    if (typeof window.Swiper === 'function') {
      swiper = new window.Swiper(main, {
        slidesPerView:1, spaceBetween:0, initialSlide:active, speed:reducedMotion() ? 0 : 300,
        watchOverflow:true, grabCursor:slides.length > 1, autoHeight:false,
        on:{ slideChange() { active = this.activeIndex; paint(true); } }
      });
    }
    instances.set(root, { select, destroy() { pauseMedia(-1); lightbox?.close(); swiper?.destroy(true, true); } });
    thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => select(index)));
    previous?.addEventListener('click', () => select(active - 1));
    next?.addEventListener('click', () => select(active + 1));
    rail?.addEventListener('scroll', paintRail, { passive:true });
    railPrevious?.addEventListener('click', () => rail.scrollBy({left:-rail.clientWidth * .8,behavior:reducedMotion() ? 'auto' : 'smooth'}));
    railNext?.addEventListener('click', () => rail.scrollBy({left:rail.clientWidth * .8,behavior:reducedMotion() ? 'auto' : 'smooth'}));
    if (rail && window.ResizeObserver) new ResizeObserver(paintRail).observe(rail);

    root.addEventListener('keydown', event => {
      if (lightbox || event.altKey || event.ctrlKey || event.metaKey || event.target.closest('[data-gallery-player]')) return;
      let target;
      if (event.key === 'ArrowRight') target = active + 1;
      else if (event.key === 'ArrowLeft') target = active - 1;
      else if (event.key === 'Home') target = 0;
      else if (event.key === 'End') target = slides.length - 1;
      if (typeof target !== 'number') return;
      event.preventDefault();
      const focusOrigin = event.target.hasAttribute('data-gallery-thumb') ? true : event.target.closest('[data-gallery-slide]') ? 'media' : false;
      select(target, focusOrigin);
    });

    root.querySelectorAll('[data-gallery-play]').forEach(button => button.addEventListener('click', () => {
      const slide = button.closest('[data-gallery-slide]');
      const player = slide.querySelector('[data-gallery-player]');
      if (!player.hasChildNodes()) player.append(slide.querySelector('[data-gallery-player-template]').content.cloneNode(true));
      button.hidden = true;
      player.hidden = false;
      const video = player.querySelector('video');
      if (video) {
        video.tabIndex = 0;
        video.focus({ preventScroll:true });
        video.play().catch(() => {}); // Only this explicit customer action starts playback.
      }
      else player.querySelector('iframe, model-viewer')?.focus();
    }));

    root.querySelectorAll('[data-gallery-zoom]').forEach(button => button.addEventListener('click', () => {
      const shell = root.querySelector('.pswp');
      if (!shell || !window.PhotoSwipe || !window.PhotoSwipeUI_Default) return;
      const buttons = Array.from(root.querySelectorAll('[data-gallery-zoom]'));
      const items = buttons.map(imageButton => {
        const dimension = imageButton.dataset.dimension.split('x').map(Number);
        const image = imageButton.querySelector('img');
        return { src:imageButton.dataset.zoom, w:dimension[0], h:dimension[1], msrc:image.dataset.gallerySrc || image.currentSrc,
          mediaId:imageButton.dataset.mediaId, el:imageButton };
      });
      pauseMedia(-1);
      lightbox = new window.PhotoSwipe(shell, window.PhotoSwipeUI_Default, items, {
        index:buttons.indexOf(button), history:false, shareEl:false, closeOnScroll:false, preload:[1,1],
        showAnimationDuration:reducedMotion() ? 0 : 250, hideAnimationDuration:reducedMotion() ? 0 : 250,
        getThumbBoundsFn(index) {
          const box = items[index].el.querySelector('img').getBoundingClientRect();
          return { x:box.left, y:box.top + window.scrollY, w:box.width };
        }
      });
      lightbox.listen('afterChange', () => {
        const id = items[lightbox.getCurrentIndex()].mediaId;
        select(slides.findIndex(slide => slide.dataset.mediaId === id));
      });
      lightbox.listen('destroy', () => {
        lightbox = null;
        focusActiveMedia();
      });
      lightbox.init();
    }));

    document.addEventListener('product-variant-updated', event => {
      const media = event.detail?.selected?.featured_media;
      if (!media) return;
      const index = slides.findIndex(slide => slide.dataset.mediaId === String(media.id));
      if (index >= 0) select(index);
    });
    document.addEventListener('visibilitychange', () => { if (document.hidden) pauseMedia(-1); });
    if (!swiper) select(active);
    paint(false);
  }

  function mount(scope = document) {
    scope.querySelectorAll('[data-mt-product-gallery]').forEach(init);
  }
  window.MTProductGallery = { mount };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount());
  else mount();
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-mt-product-gallery]').forEach(root => instances.get(root)?.destroy()));
})();
